import { useState } from 'react';
import { usePanelToast } from '../../../context/PanelToastContext';
import { applyPaymentBalance } from '../../../services/receptionist/receptionistApi';
import {
  defaultPaymentSplits,
  normalizeSplitRows,
  serializePaymentSplits,
  sumPaymentSplits,
} from '../../../utils/splitPaymentUtils';
import { formatCurrency } from '../receptionistData';
import { notifyPaymentsUpdated } from '../paymentStore';
import SplitPaymentFields from './SplitPaymentFields';
import { saleRowLeft } from './salePaymentUtils';
import { buildRemainingClearReceipt } from './buildSaleReceipt';
import '../payments.css';
import './ancillarySales.css';

function todayDate() {
  return new Date().toISOString().slice(0, 10);
}

/** Scale method splits so they sum to `target` (rounding fixed on last method). */
function scaleSplitsToAmount(splits, target) {
  const rows = normalizeSplitRows(splits);
  const sourceTotal = rows.reduce((sum, row) => sum + row.amount, 0);
  if (sourceTotal <= 0 || target <= 0) return [];
  const scaled = rows.map((row) => ({
    method: row.method,
    amount: Math.floor((row.amount / sourceTotal) * target * 100) / 100,
  }));
  const scaledSum = scaled.reduce((sum, row) => sum + row.amount, 0);
  const diff = Math.round((target - scaledSum) * 100) / 100;
  if (scaled.length && Math.abs(diff) >= 0.01) {
    scaled[scaled.length - 1].amount = Math.round((scaled[scaled.length - 1].amount + diff) * 100) / 100;
  }
  return scaled.filter((row) => row.amount > 0);
}

/**
 * Pay one or more pending sale balances in one form.
 * Installment is applied FIFO across selected rows (oldest first).
 */
export default function PaySelectedBalancesModal({
  paymentRows,
  categoryLabel,
  onClose,
  onPaid,
}) {
  const toast = usePanelToast();
  const [splits, setSplits] = useState(defaultPaymentSplits());
  const [date, setDate] = useState(todayDate());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const rows = (paymentRows || []).filter((row) => saleRowLeft(row) > 0);
  if (!rows.length) return null;

  const totalLeft = rows.reduce((sum, row) => sum + saleRowLeft(row), 0);
  const installment = sumPaymentSplits(splits);
  const memberName = rows[0]?.member || '—';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (totalLeft <= 0) {
      toast.showError('Nothing left to pay.');
      return;
    }
    if (!Number.isFinite(installment) || installment <= 0) {
      toast.showError('Enter amounts for at least one payment method.');
      return;
    }
    if (installment > totalLeft + 0.001) {
      toast.showError(`Payment cannot exceed ${formatCurrency(totalLeft)} remaining.`);
      return;
    }

    setSaving(true);
    try {
      const ordered = [...rows].sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
      let remaining = installment;
      const updatedRows = [];
      const receipts = [];

      for (const row of ordered) {
        if (remaining <= 0.001) break;
        const left = saleRowLeft(row);
        if (left <= 0) continue;
        const payAmount = Math.min(left, remaining);
        const portionSplits = scaleSplitsToAmount(splits, payAmount);
        if (!portionSplits.length) continue;

        const updated = await applyPaymentBalance({
          paymentDbId: row.rawId,
          additionalSplits: serializePaymentSplits(portionSplits),
          date,
          paymentNote: note || (rows.length > 1 ? 'Multi-balance payment' : 'Balance payment'),
        });
        updatedRows.push(updated);
        receipts.push(buildRemainingClearReceipt(updated, {
          installmentAmount: payAmount,
          installmentSplits: portionSplits,
          transactionDate: date,
          balanceBefore: left,
          pendingAfterPay: Math.max(0, left - payAmount),
          saleMeta: { itemFieldLabel: 'Package' },
        }));
        remaining = Math.round((remaining - payAmount) * 100) / 100;
      }

      notifyPaymentsUpdated();
      toast.showSaved(
        rows.length > 1
          ? `Paid ${formatCurrency(installment)} across ${updatedRows.length} sales`
          : 'Balance payment recorded',
      );
      onPaid?.(updatedRows, receipts);
      onClose();
    } catch (err) {
      toast.showError(err.message || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="subscription-modal-overlay rec-pay-balance-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="subscription-modal"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="subscription-modal-header">
          <h2 className="subscription-modal-title">
            {rows.length > 1 ? 'Pay selected balances' : 'Pay remaining balance'}
          </h2>
          <button
            type="button"
            className="subscription-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="rec-payment-receipt-preview rec-pay-balance-summary">
          <div className="rec-payment-receipt-row">
            <span>Member</span>
            <strong>{memberName}</strong>
          </div>
          <div className="rec-payment-receipt-row">
            <span>Selected sales</span>
            <strong>{rows.length}{categoryLabel ? ` · ${categoryLabel}` : ''}</strong>
          </div>
          <div className="rec-pay-selected-list">
            {rows.map((row) => (
              <div key={row.rawId || row.id} className="rec-payment-receipt-row">
                <span>
                  {categoryLabel || row.package || 'Sale'} · {String(row.date || '').slice(0, 10) || '—'}
                  <br />
                  <small>{row.package || '—'}</small>
                </span>
                <strong className="finance-amount-negative">{formatCurrency(saleRowLeft(row))}</strong>
              </div>
            ))}
          </div>
          <div className="rec-payment-receipt-row rec-payment-receipt-row-highlight">
            <span>Total balance due</span>
            <strong>{formatCurrency(totalLeft)}</strong>
          </div>
        </div>

        <form className="subscription-form" onSubmit={handleSubmit}>
          <SplitPaymentFields
            idPrefix="pay-selected"
            splits={splits}
            onChange={setSplits}
            disabled={saving}
          />
          <div className="rec-sale-payment-summary">
            <p className="owner-placeholder-text" style={{ margin: 0, fontSize: 13 }}>
              This payment: <strong>{formatCurrency(installment)}</strong>
            </p>
            <p className="rec-sale-pending-amount">
              Pending after pay:{' '}
              <strong>{formatCurrency(Math.max(0, totalLeft - installment))}</strong>
            </p>
          </div>

          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="pay-selected-date">Payment date</label>
            <input
              id="pay-selected-date"
              type="date"
              className="owner-form-input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              disabled={saving}
            />
          </div>

          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="pay-selected-note">Note (optional)</label>
            <input
              id="pay-selected-note"
              className="owner-form-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Balance payment note"
              disabled={saving}
            />
          </div>

          <div className="subscription-form-actions">
            <button type="submit" className="owner-btn owner-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : `Pay ${formatCurrency(installment || totalLeft)}`}
            </button>
            <button
              type="button"
              className="owner-btn owner-btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
